// src/features/schedule/components/NowCard.tsx
import React, { useState, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { ScheduleItem, isTimeBox, isEvent } from '../../../types/scheduleItem';
import { format } from 'date-fns';
import { Clock, MapPin } from '../../../components/common/icons';
import TimeBoxPattern from '../../../components/common/TimeBoxPattern';
import RelatedTasksSection from './RelatedTasksSection';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { useCurrentTime } from '../../../hooks/useCurrentTime';
import { TimeBoxCategory } from '../../../types/timeBox';
import { EventCategory } from '../../../types/calendar-event';
import {
  getTimeBoxCategoryConfig,
  getEventCategoryConfig
} from '../../../constants/theme';
import { formatDuration } from '../../../utils/formatters';
import { UrgencyLevel } from './TimeDisplay';

import MobileDots from './MobileDots';

interface NowCardProps {
  currentItem: ScheduleItem;
  isLoading?: boolean;
  onAdjustTimeBox?: (id: string) => void;
  onAdjustEvent?: (id: string) => void;
}

const NowCard: React.FC<NowCardProps> = ({
  currentItem,
  onAdjustTimeBox,
  onAdjustEvent,
}) => {
  const isDesktop = useMediaQuery('(min-width: 768px)');

  // 桌面版計時器內框動畫 - 測量尺寸以計算 SVG stroke-dasharray 周長
  const timerInnerRef = useRef<HTMLDivElement>(null);
  const [timerDims, setTimerDims] = useState<{
    w: number; h: number; half: number; full: number;
  } | null>(null);

  // 剩餘時間計算 - 每分鐘自動更新
  const currentTime = useCurrentTime();

  const remainingMs = useMemo(() => {
    if (!currentItem?.endTime) return null;
    const endTime = new Date(currentItem.endTime);
    return Math.max(0, endTime.getTime() - currentTime.getTime());
  }, [currentItem?.endTime, currentTime]);

  const remainingText = remainingMs !== null ? formatDuration(remainingMs) : null;

  // remainingText 從 null 變有值時，Timer block 才插入 DOM，需重新 measure
  // 用 hasTimer 布林值當 dep，避免每分鐘 remainingText 字串變化都重建 observer
  const hasTimer = remainingText !== null;
  useLayoutEffect(() => {
    if (!hasTimer) return;
    const el = timerInnerRef.current;
    if (!el) return;
    const measure = () => {
      const { width, height } = el.getBoundingClientRect();
      if (width > 0 && height > 0) {
        const rx = 8; // rounded-lg = 8px
        const perimeter = 2 * (width + height - 4 * rx) + 2 * Math.PI * rx;
        setTimerDims({ w: width, h: height, half: perimeter / 2, full: perimeter });
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasTimer]);

  // 三級緊急狀態（取代舊的 isEndingSoon 二元邏輯）
  const urgencyLevel: UrgencyLevel = useMemo(() => {
    if (remainingMs === null) return 'normal';
    if (remainingMs <= 60 * 1000) return 'critical';  // 1 分鐘內
    if (remainingMs <= 5 * 60 * 1000) return 'warning'; // 5 分鐘內
    return 'normal';
  }, [remainingMs]);

  // 計算進度百分比（已過時間 / 總時間）
  const elapsedPercent = useMemo(() => {
    if (!currentItem?.startTime || !currentItem?.endTime || remainingMs === null) return 0;
    const totalMs = new Date(currentItem.endTime).getTime() - new Date(currentItem.startTime).getTime();
    if (totalMs <= 0) return 0;
    const elapsed = totalMs - remainingMs;
    return Math.min(100, Math.max(0, (elapsed / totalMs) * 100));
  }, [currentItem?.startTime, currentItem?.endTime, remainingMs]);

  // 獲取 Pattern 類別
  const getPatternCategory = (item: ScheduleItem): TimeBoxCategory | EventCategory | 'empty' => {
    if (isTimeBox(item)) {
      return item.category;
    }
    if (isEvent(item)) {
      return item.category;
    }
    return 'empty';
  };

  // 處理卡片點擊 - 直接進入編輯
  const handleCardClick = useCallback(() => {
    if (isTimeBox(currentItem) && onAdjustTimeBox) {
      onAdjustTimeBox(currentItem.id);
    } else if (isEvent(currentItem) && onAdjustEvent) {
      onAdjustEvent(currentItem.id);
    }
  }, [currentItem, onAdjustTimeBox, onAdjustEvent]);

  // 顯示當前項目
  const formattedTimeRange = currentItem.endTime
    ? `${format(new Date(currentItem.startTime), 'HH:mm')}–${format(new Date(currentItem.endTime), 'HH:mm')}`
    : format(new Date(currentItem.startTime), 'HH:mm');

  const patternCategory = getPatternCategory(currentItem);
  const patternOpacity = isEvent(currentItem) ? 0.08 : 0.06;

  // meta 標籤資訊
  let categoryConfig;
  let scheduleTypeLabel: string;
  if (isEvent(currentItem)) {
    categoryConfig = getEventCategoryConfig(currentItem.category);
    scheduleTypeLabel = 'Event';
  } else if (isTimeBox(currentItem)) {
    categoryConfig = getTimeBoxCategoryConfig(currentItem.category);
    scheduleTypeLabel = 'Timebox';
  } else {
    categoryConfig = { label: 'Unknown', color: '#64748B', dotColor: '#64748B' };
    scheduleTypeLabel = 'Unknown';
  }

  // Timer urgency 色系
  // 軌道描邊顏色（urgency 對應的 indigo/amber/red 200）
  const orbitStrokeColor: Record<UrgencyLevel, string> = {
    normal: '#C7D2FE', // sky-200
    warning: '#FDE68A', // amber-200
    critical: '#FECACA', // red-200
  };

  const timerColorMap: Record<UrgencyLevel, { text: string; bg: string; border: string }> = {
    normal: { text: 'text-sky-600', bg: 'bg-sky-50', border: 'border-sky-200' },
    warning: { text: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
    critical: { text: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
  };
  const timerColors = timerColorMap[urgencyLevel];

  const noteText = currentItem.notes;

  return (
    <div
      className="relative overflow-hidden rounded-xl md:rounded-2xl bg-white shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer border border-black/[0.06]"
      onClick={handleCardClick}
    >
      <TimeBoxPattern category={patternCategory} opacity={patternOpacity} />

      {/* card-top：左側資訊 + 右側 Timer（桌面並排 / 行動疊加）*/}
      <div className="relative z-content flex items-start gap-4 p-5 md:p-7 pb-3 md:pb-4">
        {/* 左欄：meta + 標題 + 時間範圍 */}
        <div className="flex-1 min-w-0">
          {/* meta row */}
          <div className="flex items-center gap-1.5 mb-3">
            <span
              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: categoryConfig.dotColor }}
            />
            <span
              className="text-[11px] font-semibold uppercase tracking-wider"
              style={{ color: categoryConfig.color }}
            >
              {scheduleTypeLabel}
            </span>
            <span className="text-[11px] text-gray-300 mx-0.5">·</span>
            <span
              className="text-[11px] font-semibold uppercase tracking-wider"
              style={{ color: categoryConfig.color }}
            >
              {categoryConfig.label}
            </span>
          </div>

          {/* 標題 */}
          <h2 className="text-xl md:text-2xl font-semibold text-gray-900 leading-snug mb-3">
            {currentItem.title}
          </h2>

          {/* 時間範圍 + 位置 */}
          <div className="flex items-center gap-4 text-gray-500">
            <div className="flex items-center gap-1.5">
              <Clock size={13} className="text-gray-400 flex-shrink-0" />
              <span className="text-sm">{formattedTimeRange}</span>
            </div>
            {currentItem.location && (
              <div className="flex items-center gap-1.5">
                <MapPin size={14} className="text-gray-400 flex-shrink-0" />
                <span className="text-sm">{currentItem.location}</span>
              </div>
            )}
          </div>
        </div>

        {/* 桌面版 Timer block（行動版隱藏） */}
        {remainingText && (
          <div
            className={`hidden md:flex flex-shrink-0 flex-col items-center justify-center p-1 rounded-xl w-[180px] ${timerColors.bg}`}
            role="timer"
            aria-label={`${remainingText} remaining`}
            aria-live="polite"
          >
            {/* 內層：軌道動畫描邊 */}
            <div
              ref={timerInnerRef}
              className="relative w-full flex flex-col items-center justify-center px-3 py-3 rounded-lg"
            >
              {/* SVG 軌道列車動畫：半圈描邊順時鐘繞行，每 5 秒到達對側 */}
              {timerDims && (
                <svg
                  className="absolute inset-0 w-full h-full pointer-events-none"
                  viewBox={`0 0 ${timerDims.w} ${timerDims.h}`}
                  fill="none"
                  aria-hidden="true"
                >
                  <rect
                    x="0.5" y="0.5"
                    width={timerDims.w - 1}
                    height={timerDims.h - 1}
                    rx="13"
                    stroke={orbitStrokeColor[urgencyLevel]}
                    strokeWidth="1"
                    strokeDasharray={`${timerDims.half} ${timerDims.half}`}
                  >
                    {/* 負值 dashoffset = 反向移動 = 順時鐘 */}
                    <animate
                      attributeName="stroke-dashoffset"
                      values={`0; ${timerDims.half}; ${timerDims.full}`}
                      keyTimes="0; 0.5; 1"
                      calcMode="spline"
                      keySplines="0.4 0 0.6 1; 0.4 0 0.6 1"
                      dur="10s"
                      repeatCount="indefinite"
                    />
                  </rect>
                </svg>
              )}
              <span className="text-[9px] font-semibold uppercase tracking-widest text-gray-400 mb-1">
                Remaining
              </span>
              <span className={`text-3xl font-bold tabular-nums leading-none ${timerColors.text}`}>
                {remainingText}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* card-body：description + tasks */}
      <div className="relative z-content px-5 md:px-7 pb-6">
        {/* Description（italic + gold left-border） */}
        {noteText && (
          <p className="text-sm text-gray-500 leading-relaxed mb-5 pl-3 border-l-2 border-sky-200/70">
            {noteText}
          </p>
        )}

        {/* Related Tasks - 僅 TimeBox 顯示 */}
        {isTimeBox(currentItem) && currentItem.taskIds && currentItem.taskIds.length > 0 && (
          <RelatedTasksSection taskIds={currentItem.taskIds} />
        )}
      </div>

      {/* 行動版 Timer Band（桌面隱藏） */}
      {remainingText && (
        <div
          className={`md:hidden flex items-center justify-between mx-5 mb-4 px-5 py-3.5 rounded-xl ${timerColors.bg}`}
          role="timer"
          aria-label={`${remainingText} remaining`}
          aria-live="polite"
        >
          {/* 左側：Remaining 與 小點動畫 */}
          <div className="flex flex-col justify-center gap-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400/90 leading-none">
              Remaining
            </span>
            <div className="flex items-end mt-0.5">
              <MobileDots dotColor={orbitStrokeColor[urgencyLevel]} />
            </div>
          </div>

          {/* 右側：時間垂直置中 */}
          <div className={`text-[32px] font-bold tabular-nums leading-none tracking-tight ${timerColors.text}`}>
            {remainingText}
          </div>
        </div>
      )}

      {/* 底部 progress bar */}
      <div className="relative z-content px-5 md:px-7 pb-5">
        <div className="h-1 rounded-full bg-gray-100 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-sky-500 to-slate-600 transition-[width] duration-1000"
            style={{ width: `${elapsedPercent}%` }}
          />
        </div>
      </div>

    </div>
  );
};

export default NowCard;
